// V2 Attachments section. Same behaviour as the shared
// PolicyChangeAttachmentUpload step, but presented with the SectionPanel
// styling (blue header + outlined body) so it matches the "What changed?"
// board. The add button lives above the table (no duplicate heading).
// Kept separate so the V1 form is unaffected.

import React from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import produce from "immer";
import { getUniqueId } from "../../../../../../../../helpers";
import { Add, Remove } from "@mui/icons-material";
import { ExtendableSelect } from "../../../../../../../../components/formsComponents/ExtendableSelect";
import { habitational_attachment } from "../../../../../../../../constants/constants";
import { FileUpload } from "../../../../../../../../features/FileUpload/components/FileUpload";
import { IBusinessPolicyChangeStepsProps } from "../../PolicyChange.types";
import { SectionPanel } from "./SectionPanel";

export const AttachmentsV2: React.FC<IBusinessPolicyChangeStepsProps> = ({
  values,
  setValues,
}) => {
  const disableDelete = values.status === "Completed";

  const handleAdd = (index: number) => () => {
    const newFiles = produce(values.attachments, (draftState) => {
      draftState.splice(index, 0, {
        key: getUniqueId(),
        description: null,
        status: "Pending",
        url: null,
        file: null,
      });
    });
    setValues({ ...values, attachments: newFiles });
  };

  const handleRemove = (index: number) => () => {
    const newFiles = produce(values.attachments, (draftState) => {
      draftState.splice(index, 1);
    });
    setValues({ ...values, attachments: newFiles });
  };

  const handleRemoveFile = (rowIndex: number) => () => {
    const newFiles = produce(values.attachments, (draftState) => {
      draftState[rowIndex].file = null;
      draftState[rowIndex].url = null;
    });
    setValues({ ...values, attachments: newFiles });
  };

  const handleSelectChange = (key: string, index: number, newValue: any) => {
    const value = newValue as string;
    const newValues = produce(values.attachments, (draftValues) => {
      draftValues[index][key as "description"] = value;
    });
    setValues({ ...values, attachments: newValues });
  };

  const handleFileUpload = (rowIndex: number, file: File | null) => {
    const newFiles = produce(values.attachments, (draftState) => {
      draftState[rowIndex].file = file;
      draftState[rowIndex].url = null;
    });
    setValues({ ...values, attachments: newFiles });
  };

  return (
    <SectionPanel
      heading="Attachments"
      sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}
      bodySx={{ flexGrow: 1 }}
    >
      {/* Add button at the top of the body, right-aligned (matches Activity). */}
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          disabled={disableDelete}
          onClick={handleAdd(values.attachments.length + 1)}
          sx={{ textTransform: "none" }}
        >
          Add Attachment
        </Button>
      </Stack>
      <Table sx={{ minWidth: 650 }} aria-label="attachments table">
        <TableHead>
          <TableRow>
            <TableCell align="left">
              <Typography fontWeight={600} variant="h4">
                #
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography fontWeight={600} variant="h4">
                Description
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography fontWeight={600} variant="h4">
                Documents
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography fontWeight={600} variant="h4"></Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {values.attachments.map((item, index) => (
            <TableRow
              key={item.key}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell sx={{ width: "5%" }}>{index + 1}</TableCell>
              <TableCell sx={{ width: "30%" }}>
                <ExtendableSelect
                  value={item.description}
                  disabled={disableDelete}
                  onChange={(newValue) => {
                    handleSelectChange("description", index, newValue);
                  }}
                  placeholder="Select"
                  options={habitational_attachment.map((business) => {
                    return { label: business, value: business };
                  })}
                />
              </TableCell>
              <TableCell sx={{ width: "30%" }}>
                <FileUpload
                  value={item.url}
                  deleteDisabled={disableDelete}
                  onChange={(newFile) => handleFileUpload(index, newFile)}
                  onDelete={handleRemoveFile(index)}
                />
              </TableCell>
              <TableCell sx={{ width: "5%" }}>
                <Box display={"flex"}>
                  <IconButton
                    disabled={disableDelete}
                    color="error"
                    onClick={handleRemove(index)}
                  >
                    <Remove fontSize="small" />
                  </IconButton>
                  <IconButton
                    disabled={disableDelete}
                    color="primary"
                    onClick={handleAdd(index + 1)}
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </SectionPanel>
  );
};
